import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  MetricDefinitionRecord,
  UpdateMetricDefinitionRequest,
} from "../MetricRecord";

import { metricsQueryKeys } from "./metricsQueryKeys";

interface Params {
  name: string;
  request: UpdateMetricDefinitionRequest;
  orgId?: string;
}

/** The definition as it was before the save changed it in the list. */
interface Context {
  before: MetricDefinitionRecord | undefined;
}

/** The fields of a definition that an update can change. */
const UPDATABLE_FIELDS = ["description", "unit"] as const;

/**
 * The newer of two of the server's records of a definition. Each save's answer
 * is the record as that save left it, so an answer that arrives after a later
 * save's answer is older than what is already shown.
 */
const newer = (
  shown: MetricDefinitionRecord | undefined,
  answer: MetricDefinitionRecord,
) =>
  shown !== undefined &&
  Date.parse(shown.modified) > Date.parse(answer.modified)
    ? shown
    : answer;

/**
 * Saves a change to a metric definition. The org's cached list shows the
 * change at once, while the save runs; a failed save puts back the fields it
 * changed, and a successful one replaces the row with the server's record,
 * unless the row already holds a newer one. The list is never refetched, so a
 * table showing it keeps its rows.
 */
export function useUpdateMetricDefinition() {
  const { metricsService } = useDomainServices();
  const queryClient = useQueryClient();

  /** Applies `change` to the definition named `name` in the org's list. */
  const updateListed = (
    orgId: string | undefined,
    name: string,
    change: (definition: MetricDefinitionRecord) => MetricDefinitionRecord,
  ) =>
    queryClient.setQueryData<MetricDefinitionRecord[]>(
      metricsQueryKeys.definitionsAll(orgId),
      (definitions) =>
        definitions?.map((definition) =>
          definition.name === name ? change(definition) : definition,
        ),
    );

  return useMutation<MetricDefinitionRecord, Error, Params, Context>({
    mutationFn: ({ name, request, orgId }) =>
      metricsService.updateMetricDefinition(name, request, { orgId }),
    onMutate: async ({ name, request, orgId }) => {
      const queryKey = metricsQueryKeys.definitionsAll(orgId);
      // A fetch of the list already under way would land the old row over
      // the new one.
      await queryClient.cancelQueries({ queryKey });
      const before = queryClient
        .getQueryData<MetricDefinitionRecord[]>(queryKey)
        ?.find((definition) => definition.name === name);
      updateListed(orgId, name, (definition) => ({
        ...definition,
        ...request,
      }));
      return { before };
    },
    onError: (_error, { name, request, orgId }, context) => {
      const before = context?.before;
      if (before === undefined) {
        return;
      }
      // Only the fields this save changed, and only while they still hold
      // its values, so a failure never undoes a later save of the same row.
      updateListed(orgId, name, (definition) => {
        const restored = { ...definition };
        for (const field of UPDATABLE_FIELDS) {
          if (
            request[field] !== undefined &&
            definition[field] === request[field]
          ) {
            restored[field] = before[field];
          }
        }
        return restored;
      });
    },
    onSuccess: (record, { request }) => {
      updateListed(record.org_id, record.name, (definition) =>
        newer(definition, record),
      );
      queryClient.setQueryData<MetricDefinitionRecord>(
        metricsQueryKeys.definition(record.name, record.org_id),
        (definition) => newer(definition, record),
      );
      // Every metric value the charts, aggregates and session pages show
      // carries its definition's unit.
      if (request.unit !== undefined) {
        for (const queryKey of [
          metricsQueryKeys.queries(record.org_id),
          metricsQueryKeys.aggregates(record.org_id),
          metricsQueryKeys.sessions(record.org_id),
        ]) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
    },
  });
}
